import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SwitchComponent } from '../../../../FormComponent';
import { DO_NOTHING } from '../../../../../utils/commonfunctions';
import styles from '../../../../../styles/automation/scriptDetails/executionmanager.module.scss';
import { ONLY_CUSTOM_ARGUMENTS, SKIP_ARGUMENTS } from '../../../constant';
import SmartInputBox from '../../../../AutomationPage/SmartInputBox';
import SmartSelectbox from '../../../../AutomationPage/SmartSelectbox';
import { setIsUnsavedChanges, setFileSuggestions } from '../../../../../features/replayslice';
import { useVariables } from '../../hooks/useVariables';
import { getExecutionFilesForDropdown } from '../../../../../utils/api/executionFileApis';
import { STATUS } from '../../../../../utils/api/sharedfunction';

function getArgsSnapshot(args) {
    if (!args || Object.keys(args).length === 0) return {};
    const obj = {};
    for (const key in args) obj[key] = args[key];
    return obj;
}

/** Normalize argument value to string for SmartInputBox/SmartSelectbox (e.g. paths array -> comma-separated string). */
function toDisplayValue(val) {
    if (val == null) return '';
    if (Array.isArray(val)) return val.map((v) => (v != null ? String(v) : '')).join(', ');
    return String(val);
}

export default function ArgumentsComponent({ selectedStep, mcpToolMap={}, handleEditStep=DO_NOTHING, readOnly=false, replayExecuting }) {
    const { variablesList, variablesListDescriptions } = useVariables();
    const fileSuggestions = useSelector((state) => state.replay?.fileSuggestions ?? []);
    const [argumentsObject, setArgumentsObject] = useState(() => getArgsSnapshot(selectedStep?.args));
    const dispatch = useDispatch();

    useEffect(() => {
        const snapshot = getArgsSnapshot(selectedStep?.args);
        if (Object.keys(snapshot).length > 0) {
            setArgumentsObject(snapshot);
        }
    }, [selectedStep?.args]);

    // Ensure file suggestions are loaded so @[id] refs can resolve to file names on first render
    useEffect(() => {
        if (fileSuggestions.length > 0) return;
        let isMounted = true;
        getExecutionFilesForDropdown()
            .then((response) => {
                if (isMounted && response.status === STATUS.SUCCESS && (response.data?.suggestions?.length ?? 0) > 0) {
                    dispatch(setFileSuggestions(response.data.suggestions));
                }
            })
            .catch(() => {});
        return () => { isMounted = false; };
    }, [dispatch, fileSuggestions.length]);

    if(!selectedStep || !selectedStep?.args || Object.keys(selectedStep?.args).length === 0) {
        return null;
    }

    const handleArgumentChange = useCallback((key, value) => {
        handleEditStep(
            'update_step_arguments', 
            selectedStep?.stepIndex, 
            { [key]: value }
        );
    }, [handleEditStep, selectedStep?.stepIndex]);

    const handleInputBoxChange = (key, value) => {
        setArgumentsObject(prev => ({...prev, [key]: value}));
        dispatch(setIsUnsavedChanges(true));
    };

    const handleInputBoxBlur = (key, value) => {
        handleArgumentChange(key, value);
    };

    const renderArgumentComponent = useCallback((argument, key) => {
        const value = argumentsObject[key];
        if (!argument) {
            return (
                <SmartInputBox
                    suggestions={variablesList}
                    descriptions={variablesListDescriptions}
                    value={value}
                    onChange={(e) => handleInputBoxChange(key, e.target.value)}
                    onBlur={(e) => handleInputBoxBlur(key, e.target.value)}
                    size="small"
                    placeholder=""
                    error={selectedStep?.argsError?.includes(key) ? `${key} is required` : ''}
                    readOnly={readOnly}
                    disabled={replayExecuting}
                />
            );
        }
        switch(argument.type) {
            case 'boolean':
                return (
                    <SwitchComponent
                        checked={value}
                        onChange={(e) => handleArgumentChange(key, e.target.checked)}
                        size="small"
                        error={selectedStep?.argsError?.includes(key) ? `${key} is required` : ''}
                        readOnly={readOnly}
                        disabled={replayExecuting}
                    />
                )
            case 'string':
            case 'array':
            case 'number':
                if(argument?.enum) {
                    return (
                        <SmartSelectbox
                            value={toDisplayValue(value)}
                            onChange={(e) => handleArgumentChange(key, e.target.value)}
                            size="small"
                            placeholder={argument?.description}
                            options={argument?.enum?.map(item => ({ value: item, label: item })) || []}
                            suggestions={variablesList}
                            descriptions={variablesListDescriptions}
                            fileSuggestions={fileSuggestions}
                            error={selectedStep?.argsError?.includes(key) ? `${key} is required` : ''}
                            readOnly={readOnly}
                            disabled={replayExecuting}
                        />
                    )
                } else {
                    return (
                        <SmartInputBox
                            suggestions={variablesList}
                            descriptions={variablesListDescriptions}
                            fileSuggestions={fileSuggestions}
                            value={toDisplayValue(value)}
                            onChange={(e) => handleInputBoxChange(key, e.target.value)}
                            onBlur={(e) => handleInputBoxBlur(key, e.target.value)}
                            size="small"
                            placeholder={argument?.description}
                            error={selectedStep?.argsError?.includes(key) ? `${key} is required` : ''}
                            readOnly={readOnly}
                            disabled={replayExecuting}
                        />
                    )
                }
        }
    }, [handleArgumentChange, argumentsObject, variablesList, variablesListDescriptions, fileSuggestions, selectedStep?.argsError, readOnly, replayExecuting]);


    return (
        <div className={styles.section}>
            <span role="title" aria-level="2" className={styles.label}>Arguments:</span>
            <div className={styles.argumentContainer}>
                {Object.keys(selectedStep?.args || {})?.map((key) => {
                    if(SKIP_ARGUMENTS.includes(key) || (ONLY_CUSTOM_ARGUMENTS.includes(key) && !selectedStep?.customStep)) {
                        return null;
                    }
                    const toolArgs = mcpToolMap[selectedStep?.tool];
                    const argument = toolArgs?.[key];
                    const required = argument?.required || (selectedStep?.customStep && ONLY_CUSTOM_ARGUMENTS.includes(key));
                    return (
                        <React.Fragment key={key}>
                            <span className={styles.argumentLabel}>{`${key}${required ? '*' : ''}`}:</span>
                            {renderArgumentComponent(argument, key)}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}